import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, MapPin } from 'lucide-react';

const AcademyPage = () => {
  const { courses, events, courseProgress } = useData();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const getCourseProgress = (course) => {
    const progress = courseProgress[course.id];
    if (!progress) return 0;
    const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons, 0);
    return Math.round((progress.completedLessons.length / totalLessons) * 100);
  };

  const variants = ['v1', 'v2', 'v3'];
  const variantColors = {
    v1: 'hsl(var(--accent-soft))',
    v2: 'hsl(var(--blue-soft))',
    v3: 'hsl(var(--violet-soft))'
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <div className="mb-8">
          <span className="eyebrow">Academy · "Learn"</span>
          <h2 className="mt-3 mb-2">Build real capability</h2>
          <p style={{ color: 'hsl(var(--ink-muted))' }}>
            Structured courses and live trainings, linked to the physical Centre of Excellence. Your progress feeds the credentials on your community profile.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Courses */}
          <div className="md:col-span-2 space-y-6">
            <h3>Course Catalog</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {courses.map((course) => {
                const progress = getCourseProgress(course);
                const moduleCount = course.modules.length;
                const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons, 0);
                
                return (
                  <Card
                    key={course.id}
                    className="p-0 overflow-hidden cursor-pointer hover:shadow-lg"
                    onClick={() => navigate(`/academy/courses/${course.id}`)}
                  >
                    <div className="h-24 flex items-center justify-center relative" style={{ backgroundColor: variantColors[course.thumbnailVariant] }}>
                      <span className="text-4xl">
                        {course.title.includes('Battery') ? '🔋' : course.title.includes('Digitisation') ? '⚙️' : course.title.includes('Procurement') ? '📊' : '🌍'}
                      </span>
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'white', color: 'hsl(var(--ink-2))' }}>
                        {course.level}
                      </span>
                    </div>
                    <div className="p-4">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'hsl(var(--violet-soft))', color: 'hsl(var(--violet))' }}>Course</span>
                      <h4 className="mt-2 mb-2">{course.title}</h4>
                      <p className="text-sm mb-3" style={{ color: 'hsl(var(--ink-muted))' }}>{course.summary}</p>
                      <div className="text-xs mb-2" style={{ color: 'hsl(var(--ink-faint))' }}>
                        {moduleCount} modules · {Math.round(course.durationMins / 60)}hrs
                      </div>
                      {progress > 0 && (
                        <>
                          <div className="w-full h-1 rounded-full mb-1" style={{ backgroundColor: 'hsl(var(--line-2))' }}>
                            <div className="h-full rounded-full" style={{ width: `${progress}%`, backgroundColor: 'hsl(var(--mint))' }} />
                          </div>
                          <div className="text-xs" style={{ color: 'hsl(var(--ink-faint))' }}>{progress}% complete</div>
                        </>
                      )}
                      {progress === 0 && course.level === 'ADVANCED' && (
                        <div className="text-xs" style={{ color: 'hsl(var(--primary))' }}>Recommended next</div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Training Calendar */}
          <div>
            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg">Training calendar</h3>
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))' }}>Centre of Excellence</span>
              </div>
              <p className="text-xs mb-4" style={{ color: 'hsl(var(--ink-muted))' }}>Register for live and in-person sessions.</p>
              
              <div className="space-y-3">
                {events.map((event) => (
                  <div key={event.id} className="flex gap-3 pb-3 border-b" style={{ borderColor: 'hsl(var(--line))' }}>
                    <div className="text-center flex-shrink-0 w-12">
                      <div className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{new Date(event.startsAt).getDate()}</div>
                      <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>{new Date(event.startsAt).toLocaleString('default', { month: 'short' })}</div>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold mb-1">{event.title}</h5>
                      <div className="flex items-center gap-1 text-xs mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>
                        <MapPin className="h-3 w-3" />
                        {event.location} · {event.mode}
                      </div>
                      {event.seatsRemaining && (
                        <div className="text-xs" style={{ color: 'hsl(var(--ink-faint))' }}>{event.seatsRemaining} seats left</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <ConciergeLauncher />
    </div>
  );
};

export default AcademyPage;
